import React from "react";
import { useDroppable } from "@dnd-kit/react";

import EyeIconSrc from "../../../../../../../resources/route-builder/eye.svg";
import EyeOffIconSrc from "../../../../../../../resources/route-builder/eye-off.svg";
import { ProposedRoute } from "../../../../../../../types/routeBuilder";
import { resolveRouteColorPalette } from "../../../../../../../components/Maps/ActiveRoutes/routeColor";
import { useRouteBuilder } from "../../../context/RouteBuilderContext";
import { incorporatesTime, routeSummaryText } from "../../../lib/helpers";
import { AddRouteButton, RemoveRouteButton } from "./RouteActions";
import { groupBuilderRoutes } from "../../../lib/builderRouteGroups";

function EyeIcon({ hidden }: { hidden: boolean }) {
  return (
    <img
      src={hidden ? EyeOffIconSrc : EyeIconSrc}
      alt=""
      className="h-3.5 w-3.5"
      aria-hidden
    />
  );
}

function RouteTab({ route }: { route: ProposedRoute }) {
  const {
    session,
    selectedRouteId,
    setSelectedRouteId,
    dirtyRouteIds,
    isRouteMapVisible,
    toggleRouteMapVisibility,
  } = useRouteBuilder();

  const { ref, isDropTarget } = useDroppable({
    id: `route-drop-${route.id}`,
    type: "route-column",
    accept: "stop",
    // Beat stop-slot (30) / stop (20) so left-route drops win when the pointer
    // is over a route tab.
    collisionPriority: 40,
    data: { routeId: route.id },
  });

  const palette = resolveRouteColorPalette(route.color);
  const isSelected = selectedRouteId === route.id;
  const isDirty = dirtyRouteIds.has(route.id);
  const mapVisible = isRouteMapVisible(route.id);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      data-testid={`route-tab-${route.id}`}
      className={`flex w-full shrink-0 cursor-pointer flex-col rounded-xl border py-2.5 pl-3 pr-1.5 text-left transition md:w-full ${
        !mapVisible ? "opacity-50" : ""
      } ${
        isDropTarget
          ? "border-sky-400 bg-sky-50 ring-2 ring-sky-200"
          : isSelected
            ? "border-snow-primary bg-sky-50"
            : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
      onClick={() => setSelectedRouteId(route.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setSelectedRouteId(route.id);
        }
      }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${palette.bg}`} />
        <span
          data-testid={`route-name-${route.id}`}
          className="min-w-0 flex-1 truncate text-sm font-semibold text-snow-primary"
        >
          {route.name}
        </span>
        {route.stops.length === 0 || isDirty ? (
          <div className="flex shrink-0 items-center gap-0.5">
            {route.stops.length === 0 ? (
              <RemoveRouteButton route={route} />
            ) : null}
            {isDirty ? (
              <span
                data-testid={`route-dirty-${route.id}`}
                title="Re-optimize"
                aria-label="Re-optimize"
                className="flex h-5 w-5 shrink-0 items-center justify-center"
              >
                <span className="h-2 w-2 rounded-full bg-sky-500" aria-hidden />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
        <span>
          {routeSummaryText(route, {
            incorporateTime: incorporatesTime(session?.inputs),
          })}
        </span>
        <button
          type="button"
          data-testid={`route-visibility-${route.id}`}
          className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200/80 hover:text-slate-800"
          aria-label={mapVisible ? `Hide ${route.name}` : `Show ${route.name}`}
          aria-pressed={mapVisible}
          title={mapVisible ? "Hide on map" : "Show on map"}
          onClick={(e) => {
            e.stopPropagation();
            toggleRouteMapVisibility(route.id);
          }}
        >
          <EyeIcon hidden={!mapVisible} />
        </button>
      </div>
    </div>
  );
}

export default function RoutesSidebar() {
  const { session, visibleRoutes } = useRouteBuilder();
  if (!session) return null;
  const groups = groupBuilderRoutes(visibleRoutes, session);

  return (
    <aside
      className="flex shrink-0 flex-col border-slate-200 bg-white md:w-52 md:border-r"
      data-testid="routes-sidebar"
    >
      <div className="shrink-0 border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Routes
      </div>
      <div className="flex gap-2 overflow-x-auto p-2 md:flex-col md:overflow-x-hidden md:overflow-y-auto">
        {groups.map((group) => (
          <div key={group.key} data-testid={`sidebar-group-${group.key}`}>
            {group.label ? (
              <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {group.label}
              </div>
            ) : null}
            <div className="flex gap-2 md:flex-col">
              {group.routes.map((route) => (
                <div key={route.id} className="min-w-[9.5rem] md:min-w-0">
                  <RouteTab route={route} />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="min-w-[9.5rem] py-2 text-center md:min-w-0">
          <AddRouteButton />
        </div>
      </div>
    </aside>
  );
}
